import { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { isEqual } from "lodash";
import "../../AppPage.css";
import { ADAPT_METHOD_OPTIONS, FRAME_RATE_LIST } from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import GlimpseBreadCrumbs from "../../../components/glimpse/GlimpseBreadCrumbs";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { NumericInput } from "../../../components/NumericInput";
import { CancelTitleBarMenu } from "../../../components/titleBarMenu/CancelTitleBarMenu";
import { PropertySubheader } from "../../../components/PropertySubheader";
import { PercentageInput } from "../../../components/PercentageInput";
import { loadClip } from "../utils/dataLoader";
import {
  getNavigationUrl,
  checkStatusWhitelist,
} from "../../../store/navigationUtil";
import { useGlimpse } from "../../../store/glimpse/hooks";
import { useModals } from "../../../store/dialogs/hooks.js";
import { GlimpseModelLengthDialog } from "../../../components/dialogs/GlimpseModelLengthDialog.js";

const maxWidth = 300;
// minHeight allows consistent space for helper text
const minHeight = 160;

export default function GlimpsePropertiesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [clip, setClip] = useState({});
  const [started, setStarted] = useState(false);
  const [initialState, setInitialState] = useState({});
  const { openGlimpseModelLengthDialog } = useModals();

  const {
    adaptMethod,
    modelAudioDuration,
    modelCaseId,
    modelClipId,
    frameSizeLow,
    frameSizeHi,
    gateLevelGF,
    midModelLengthGF,
    modelClipSnapshot,
    utteranceLength,
    gateLevelD,
    midModelLengthD,
    lowPass,
    highPass,
    arrayWidth,
    isValid,
    isFieldValid,
    getFieldErrors,
    queueRequest,
    update: glimpseUpdate,
    reset: glimpseReset,
    resetParameters,
    getProcessingParameters,
  } = useGlimpse();

  const { caseId, clipId } = useParams();
  const { minLength } = modelClipSnapshot || {};

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (!checkStatusWhitelist(currClip, "EDITING")) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    setClip(currClip);
    if (!modelCaseId || !modelClipId) {
      navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`);
    }
    if (currClip.arrayWidth)
      glimpseUpdate({ arrayWidth: currClip.arrayWidth.toString() });
  }, [caseId, clipId, navigate, modelCaseId, modelClipId, glimpseUpdate]);

  const handleQueueGlimpse = async () => {
    setStarted(true);
    await queueRequest(
      modelCaseId,
      modelClipId,
      caseId,
      clipId,
      getProcessingParameters(),
    );
    const url = `/app/cases/${caseId}/clips/${clipId}`;
    setStarted(false);
    navigate(url);
  };

  const handleRun = () => {
    if (frameSizeHi > minLength) {
      openGlimpseModelLengthDialog();
      return;
    }
    handleQueueGlimpse();
  };

  useEffect(() => {
    load();
  }, [load]);

  const isUpdated = isEqual(
    initialState,
    Object.entries({
      adaptMethod,
      frameSizeLow,
      frameSizeHi,
      gateLevelGF,
      midModelLengthGF,
      utteranceLength,
      gateLevelD,
      midModelLengthD,
      lowPass,
      highPass,
      arrayWidth,
    }).reduce((acc, [key, value]) => {
      acc[key] = Number(value);
      return acc;
    }, {}),
  );

  useEffect(() => {
    setInitialState(
      Object.entries({
        adaptMethod,
        frameSizeLow,
        frameSizeHi,
        gateLevelGF,
        midModelLengthGF,
        utteranceLength,
        gateLevelD,
        midModelLengthD,
        lowPass,
        highPass,
        arrayWidth,
      }).reduce((acc, [key, value]) => {
        acc[key] = Number(value);
        return acc;
      }, {}),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} p={2}>
        <GlimpseBreadCrumbs active={2} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Selected Parameters
        </Typography>
        <PropertySubheader value="ADAPTIVE FILTER PROPERTIES" />
        <Box
          display="flex"
          justifyContent="center"
          columnGap={2}
          p={1}
          rowGap={4}
          flexWrap="wrap"
        >
          <FormControl
            error={!isFieldValid("frameSizeLow")}
            fullWidth
            sx={{ maxWidth: { sm: maxWidth }, minHeight: { sm: minHeight } }}
          >
            <InputLabel htmlFor="frame-size-low" id="frame-size-low-label">
              Frame Size (Focusing Aperture) Low
            </InputLabel>
            <Select
              inputProps={{ id: "frame-size-low" }}
              labelId="frame-size-low-label"
              label="Frame Size (Focusing Aperture) Low"
              value={frameSizeLow}
              onChange={(event) =>
                glimpseUpdate({ frameSizeLow: Number(event.target.value) })
              }
            >
              {FRAME_RATE_LIST.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    {data.label}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>
              {isFieldValid("frameSizeLow")
                ? "Minimum processing frame size in samples."
                : getFieldErrors("frameSizeLow").join(", ")}
            </FormHelperText>
          </FormControl>
          <FormControl
            error={!isFieldValid("frameSizeHi")}
            fullWidth
            sx={{ maxWidth: { sm: maxWidth }, minHeight: { sm: minHeight } }}
          >
            <InputLabel htmlFor="frame-size-hi" id="frame-size-hi-label">
              Frame Size (Focusing Aperture) High
            </InputLabel>
            <Select
              inputProps={{ id: "frame-size-hi" }}
              labelId="frame-size-hi-label"
              label="Frame Size (Focusing Aperture) High"
              value={frameSizeHi}
              onChange={(event) =>
                glimpseUpdate({ frameSizeHi: Number(event.target.value) })
              }
            >
              {FRAME_RATE_LIST.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    {data.label}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>
              {isFieldValid("frameSizeHi")
                ? "Maximum processing frame size in samples. Set equal\nto minimum frame size if only the single size is desired."
                : getFieldErrors("frameSizeHi").join(", ")}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          columnGap={2}
          p={1}
          rowGap={4}
          flexWrap="wrap"
        >
          <FormControl
            fullWidth
            sx={{ maxWidth: { sm: maxWidth }, minHeight: { sm: minHeight } }}
          >
            <InputLabel htmlFor="adapt-method" id="adapt-method-label">
              Adapt Method
            </InputLabel>
            <Select
              inputProps={{ id: "adapt-method" }}
              labelId="adapt-method-label"
              label="Adapt Mehod"
              value={adaptMethod}
              onChange={(event) =>
                glimpseUpdate({ adaptMethod: Number(event.target.value) })
              }
            >
              {ADAPT_METHOD_OPTIONS.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    <Tooltip title={data.tooltipText} placement="left" arrow>
                      <Typography sx={{ width: "100%" }}>
                        {data.label}
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        <Accordion
          sx={{
            border: "none",
            backgroundColor: theme.palette.background.default,
            boxShadow: "none",
            mt: 4,
            pb: 12,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <PropertySubheader value="Advanced" />
          </AccordionSummary>
          <AccordionDetails>
            <Box
              display="flex"
              justifyContent="center"
              columnGap={2}
              p={1}
              rowGap={4}
              flexWrap="wrap"
            >
              <NumericInput
                label="Gate Level GF"
                value={gateLevelGF}
                valid={isFieldValid("gateLevelGF")}
                errorText={getFieldErrors("gateLevelGF").join(", ")}
                caption="Controls the threshold under which softer sounds are excluded from the desired source location’s spatial model. Higher settings exclude more soft sounds."
                onChanged={(value) =>
                  glimpseUpdate({ gateLevelGF: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <PercentageInput
                decimal={2}
                pctDecimal={0}
                label="Minimum Model Length GF"
                value={midModelLengthGF}
                valid={isFieldValid("midModelLengthGF")}
                errorText={getFieldErrors("midModelLengthGF").join(", ")}
                baseNumber={modelAudioDuration}
                caption="Process this percent of modeling data."
                onChanged={(value) =>
                  glimpseUpdate({ midModelLengthGF: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <PropertySubheader value="DELAY ESTIMATOR PARAMETERS" />
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              columnGap={2}
              p={1}
              rowGap={4}
              flexWrap="wrap"
            >
              <NumericInput
                label="Utterance Length"
                value={utteranceLength}
                valid={isFieldValid("utteranceLength")}
                errorText={getFieldErrors("utteranceLength").join(", ")}
                caption="Minimum expected length in ms of useful modeling sound."
                onChanged={(value) =>
                  glimpseUpdate({ utteranceLength: Number(value) })
                }
                right="ms"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <NumericInput
                label="Gate Level D"
                value={gateLevelD}
                valid={isFieldValid("gateLevelD")}
                errorText={getFieldErrors("gateLevelD").join(", ")}
                caption="Controls the threshold under which softer sounds are excluded from the desired source location delay estimator. Higher settings exclude more soft sounds."
                onChanged={(value) =>
                  glimpseUpdate({ gateLevelD: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <PercentageInput
                decimal={2}
                pctDecimal={0}
                baseNumber={modelAudioDuration}
                label="Minimum Model Length D"
                value={midModelLengthD}
                valid={isFieldValid("midModelLengthD")}
                errorText={getFieldErrors("midModelLengthD").join(", ")}
                caption="Process this percent of modeling data."
                onChanged={(value) =>
                  glimpseUpdate({ midModelLengthD: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              columnGap={2}
              p={1}
              rowGap={4}
              flexWrap="wrap"
            >
              <NumericInput
                label="High Pass"
                value={highPass}
                valid={isFieldValid("highPass")}
                errorText={getFieldErrors("highPass").join(", ")}
                caption="Attenuate below this frequency (in Hz) for delay estimator."
                onChanged={(value) =>
                  glimpseUpdate({ highPass: Number(value) })
                }
                right="Hz"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <NumericInput
                label="Low Pass"
                value={lowPass}
                valid={isFieldValid("lowPass")}
                errorText={getFieldErrors("lowPass").join(", ")}
                caption="Attenuate above this frequency (in Hz) for delay estimator."
                onChanged={(value) => glimpseUpdate({ lowPass: Number(value) })}
                right="Hz"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
            </Box>
            <Box display="flex" justifyContent="center" p={1}>
              <NumericInput
                label="Array Width"
                value={arrayWidth}
                valid={isFieldValid("arrayWidth")}
                errorText={getFieldErrors("arrayWidth").join(", ")}
                caption="Controls maximum search space. For a planar array, this can be half the actual array width if the desired source location is in the far field and < 30° off-axis. For a solid spherical array, multiply by π/2  if the desired source location is in the near field."
                onChanged={(value) =>
                  glimpseUpdate({ arrayWidth: Number(value) })
                }
                right="cm"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight + 20 },
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          position: "fixed",
          transform: "translateX(-50%)",
          bottom: 64,
          mb: 1,
          left: "50%",
          zIndex: 1,
          width: "calc(100% - 1rem)",
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => {
            await glimpseReset();
            navigate(`/app/cases/${caseId}/clips/${clipId}/editor`);
          }}
        >
          CANCEL
        </Button>
        <Box display="flex" gap={2}>
          <Button
            disabled={isUpdated}
            variant="outlined"
            onClick={async () => {
              await resetParameters();
            }}
          >
            RESET
          </Button>
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            disabled={!isValid || started}
            onClick={handleRun}
          >
            {started ? "RUNNING" : "RUN GLIMPSE"}
          </Button>
        </Box>
      </Paper>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar
          menu={<CancelTitleBarMenu clipId={clipId} caseId={caseId} />}
          name={clip?.description}
          label={clip?.description ? "Create GLIMPSE Job" : ""}
          onBack={() =>
            navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`)
          }
        />
      </Box>
      <GlimpseModelLengthDialog
        frameSizeHi={frameSizeHi}
        minLength={minLength}
        handleQueueGlimpse={handleQueueGlimpse}
      />
    </Stack>
  );
}
